import { Injectable } from '@angular/core';
import { OemIntegrationService } from '@quorum/quorum-api';
import { BehaviorSubject, Observable } from 'rxjs';
import { MotorTaxonomyResponse } from '@quorum/models/oem-resource';

// MOTOR LiteralName ends with an action verb describing the procedure type. Items ending with these
// suffixes are non-billable procedures (inspections, lubrications, torque checks, etc.) that never
// map to a DMS op code, so they're hidden from the mapping settings UI to reduce clutter.
const NON_BILLABLE_LITERAL_NAME_SUFFIXES: readonly string[] = [
  'Inspect',
  'Inspection',
  'Lubricate',
  'Torque',
  'Test',
  'Access',
];

const isNonBillableTaxonomyItem = (literalName: string): boolean =>
  NON_BILLABLE_LITERAL_NAME_SUFFIXES.some((suffix) => literalName.endsWith(suffix));

export interface LinkedDmsService {
  opCode: string;
  serviceGroupName: string;
}

export interface MotorServiceMapping {
  taxonomyId: number;
  motorServiceName: string;
  linkedServices: LinkedDmsService[];
}

export interface MotorServiceCategory {
  name: string;
  collapsed: boolean;
  items: MotorServiceMapping[];
}

@Injectable()
export class MotorTaxonomyService {
  private _categories$ = new BehaviorSubject<MotorServiceCategory[] | null>(null);
  private _isLoading$ = new BehaviorSubject<boolean>(false);
  private _hasError$ = new BehaviorSubject<boolean>(false);

  readonly categories$: Observable<MotorServiceCategory[] | null> = this._categories$.asObservable();
  readonly isLoading$: Observable<boolean> = this._isLoading$.asObservable();
  readonly hasError$: Observable<boolean> = this._hasError$.asObservable();

  constructor(private oemIntegrationService: OemIntegrationService) {}

  /** Call once at app load (e.g. ShellComponent.ngOnInit). No-ops if already loading or loaded. */
  preload(): void {
    if (this._categories$.value !== null || this._isLoading$.value) return;

    this._isLoading$.next(true);
    this._hasError$.next(false);

    const queryParams = { ContentDataTypes: 'MaintenanceSchedules' };

    this.oemIntegrationService.get<MotorTaxonomyResponse[]>('motor/taxonomy', { params: queryParams }).subscribe({
      next: (data) => {
        this._categories$.next(this.transformTaxonomyData(data));
        this._isLoading$.next(false);
      },
      error: () => {
        this._hasError$.next(true);
        this._isLoading$.next(false);
      },
    });
  }

  private transformTaxonomyData(data: MotorTaxonomyResponse[]): MotorServiceCategory[] {
    return data
      .filter((entry) => entry?.ContentSilo?.ID === 33 && entry?.Taxonomy?.Systems?.length) // Only include entries that are for MaintenanceSchedules, ContentSilo 33, and have Systems data to display
      .flatMap((entry) =>
        entry.Taxonomy.Systems.map((system) => ({
          name: system.Name,
          collapsed: false,
          items: (system.Groups || [])
            .flatMap((group) => group.SubGroups || [])
            .flatMap((subGroup) => subGroup.TaxonomyInfo || [])
            .filter((info) => !isNonBillableTaxonomyItem(info.LiteralName))
            .map(
              (info): MotorServiceMapping => ({
                taxonomyId: info.TaxonomyID,
                motorServiceName: info.LiteralName,
                linkedServices: [],
              })
            )
            .sort((a, b) => a.motorServiceName.localeCompare(b.motorServiceName)),
        }))
      );
  }
}
